/**
 * useToolbarInitialization Hook
 *
 * Initializes MapToolbox currentGeometries when task form opens.
 * Handles both create mode (default marker) and edit mode (existing task geometries).
 * This is the Single Source of Truth initialization for geometry in the wizard.
 *
 * @module hooks/useToolbarInitialization
 */

import { useEffect } from 'react';
import { useGeoMarkupStore } from '../../../../store';
import { useDrawingTools } from '../../../../hooks/useDrawingTools';
import type { Task } from '../../../../types';

export const useToolbarInitialization = (editingTask?: Task | null) => {
  const { loadGeometries } = useDrawingTools();
  const isTaskFormOpen = useGeoMarkupStore((s) => s.tasks.formIsOpen);

  useEffect(() => {
    if (!isTaskFormOpen) return;

    // Load all geometries for editing (multi-geometry support: full array loaded)
    // NOTE: loadGeometries preserves isDrawing and activeGeometryId state
    // The caller (e.g., openTaskForEditWithGeometry) manages drawing state explicitly
    if (editingTask?.geometries && editingTask.geometries.length > 0) {
      // Edit mode: load all existing geometries
      loadGeometries(editingTask.geometries);
    }

    // Note: Create mode initialization is now handled by handleCreateTaskHere
    // which calls addGeometry() with clicked coordinates directly (SSOT pattern)
    // Note: hideOtherMarkers auto-controlled by uiSlice (enabled on form open, disabled on close)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTaskFormOpen, editingTask?.id]);
};
